/**
 * The first week Du Chef proposes, built from the taste axes and the confirmed
 * allergens with the planner's own dinner generator.
 *
 * `pickOnboardingDinners` is pure and synchronous (tested). `toPlannedMeals`
 * turns the picks into meal-plan rows; the caller fetches recipe details first
 * so instructions are on the row and `originalLanguage` is copied — without
 * it `translateMealPlanRecipes` silently skips the meal and a French cook gets
 * an English plan (ISSUE-013).
 */
import type { DinnerMVPConfig, GeneratedDinner } from './weekGeneratorService';
import { HORIZON_DAYS, horizonRange, generateHorizonDinners } from './horizonWeek';
import { isFishRecipe, isVegetarianRecipe, satisfiesDietaryRestrictions } from '../utils/dietClassifier';
import { normalizeAllergenText, recipeHasAllergen } from './allergenMatcher';
import { axesToDinnerConfig, type TasteAxes } from './tasteAxes';
import type { PersonalRecipeIndexEntry } from './personalRecipeIndex';
import type { RecipeMetadata } from '../redux/recipeMetadataSlice';
import type { MealSchedulePreferences } from '../redux/userPreferencesSlice';
import type { PlannedMeal, MealType } from '../redux/mealPlanSlice';
import type { Recipe } from '../types/recipe';
import { parseIngredientText, parseAmount } from '../utils/ingredientParser';
import { addDays, currentWeekStart } from '../utils/localDate';
import { plannerRecipeDetails } from './plannerLibrary';
import { decodeHtmlEntities } from '../utils/stripInstructionHtml';
import { mealIdentityOf } from './mealIdentity';

export interface OnboardingWeekInput {
  weekStart: string;
  recipes: PersonalRecipeIndexEntry[];
  metadata: Record<string, RecipeMetadata>;
  axes: TasteAxes;
  allergenTerms: string[];
  /** Excluded ingredients declared in Réglages (empty during onboarding itself). */
  dislikedIngredients?: string[];
  /** Declared diets from Réglages (empty during onboarding itself). */
  dietaryRestrictions?: DinnerMVPConfig['dietaryRestrictions'];
  schedule: MealSchedulePreferences;
  servings: number;
  /** YYYY-MM-DD; days before it are skipped when it falls inside the week. */
  todayIso: string;
  /** The app language. Tonight's ideas prefer titles the cook can read in it. */
  language?: 'en' | 'fr';
}

/**
 * The week the cook lands on: this week, unless it is already Saturday or
 * Sunday, in which case next week — nobody should meet a plan that is over.
 */
export function onboardingWeekStart(now: Date = new Date()): string {
  const start = currentWeekStart(now);
  const dow = now.getDay();
  return dow === 0 || dow === 6 ? addDays(start, 7) : start;
}

/**
 * Why a first plan came out the size it did.
 *
 * Five onboardings in 180 days produced a week with **zero** dinners, and the
 * event we had (`meal_count: 0`) could not say whether the library was empty or
 * the cook's own exclusions had eaten all 481 recipes. Those are opposite bugs
 * with opposite fixes, so the funnel is reported rather than guessed at.
 */
export interface OnboardingDiagnostics {
  /** Recipes handed to the generator. Zero means the index never loaded. */
  recipesConsidered: number;
  /** Survivors of the declared diets. */
  afterDietFilter: number;
  /** Survivors of allergens and excluded ingredients, applied after the diets. */
  afterAllergenFilter: number;
  /** How many terms that exclusion was built from — one bad term can empty the library. */
  allergenTermsCount: number;
  /** True when the taste axes had to be dropped to fill the week. */
  relaxedPassUsed: boolean;
}

/**
 * Re-walk the generator's own filters to count survivors at each step.
 *
 * Deliberately mirrors `generateDinnerOnlyPlan` (diets first, then the shared
 * allergen/disliked matcher) so the numbers describe that run rather than a
 * plausible-looking parallel one.
 */
function describeFunnel(input: OnboardingWeekInput): Omit<OnboardingDiagnostics, 'relaxedPassUsed'> {
  const terms = [
    ...(input.allergenTerms ?? []),
    ...(input.dislikedIngredients ?? []).map(normalizeAllergenText).filter(Boolean),
  ];

  const afterDiet = input.dietaryRestrictions?.length
    ? input.recipes.filter((r) => satisfiesDietaryRestrictions(r, input.dietaryRestrictions!))
    : input.recipes;

  const afterAllergen = terms.length > 0
    ? afterDiet.filter((r) => !recipeHasAllergen(r, terms))
    : afterDiet;

  return {
    recipesConsidered: input.recipes.length,
    afterDietFilter: afterDiet.length,
    afterAllergenFilter: afterAllergen.length,
    allergenTermsCount: terms.length,
  };
}

/**
 * Seven dinners at most. When the profile leaves days empty the axes are
 * relaxed, never the allergens; a day still empty stays empty.
 */
export function pickOnboardingDinners(input: OnboardingWeekInput): GeneratedDinner[] {
  return planOnboardingDinners(input).dinners;
}

/**
 * `pickOnboardingDinners`, plus why the answer came out that size.
 *
 * The horizon is **seven days from today**, not "today to Sunday". Signing up
 * on a Friday used to produce three dinners, because the generator's day slots
 * come from one Monday week and the days already gone are filtered out — 41% of
 * all generated plans came in short that way. The horizon now runs past Sunday
 * into the following week's bucket, which `mealPlanSlice` files by date anyway.
 *
 * The one exception is the weekend bump in `onboardingWeekStart`: when today is
 * outside the landing week the cook meets a clean Monday-to-Sunday week, and
 * nothing spills.
 */
export function planOnboardingDinners(
  input: OnboardingWeekInput,
): { dinners: GeneratedDinner[]; diagnostics: OnboardingDiagnostics } {
  const funnel = describeFunnel(input);
  if (input.recipes.length === 0) {
    return { dinners: [], diagnostics: { ...funnel, relaxedPassUsed: false } };
  }

  const runPass = generatorPass(input);
  const first = runPass({ ...axesToDinnerConfig(input.axes, input.schedule), tasteAxes: input.axes });
  if (first.length >= HORIZON_DAYS) {
    return { dinners: first, diagnostics: { ...funnel, relaxedPassUsed: false } };
  }

  // The relaxed pass fills the days the first left empty, so it must keep its
  // distance from what the first already chose.
  const relaxed = runPass({
    mealSchedule: input.schedule,
    previouslyGeneratedRecipeIds: first.map((d) => d.recipe.id),
    sameMealHistory: first.map((d) => ({ date: d.slot.date, identity: mealIdentityOf(d.recipe) })),
  });
  const byDate = new Map<string, GeneratedDinner>();
  for (const d of relaxed) byDate.set(d.slot.date, d);
  for (const d of first) byDate.set(d.slot.date, d);
  const dinners = [...byDate.values()].sort((x, y) => x.slot.date.localeCompare(y.slot.date));
  return { dinners, diagnostics: { ...funnel, relaxedPassUsed: dinners.length > first.length } };
}

/**
 * One run of the planner's generator over the onboarding horizon, with the
 * cook's allergens and exclusions always applied. Callers add the axes (or
 * not) and any recipes to leave out.
 */
function generatorPass(input: OnboardingWeekInput): (extra: Partial<DinnerMVPConfig>) => GeneratedDinner[] {
  // Onboarding always spills: the week on the other side is certainly empty.
  const range = horizonRange(input.weekStart, input.todayIso);
  const base: DinnerMVPConfig = {
    excludedRecipeIds: [],
    preferredServings: input.servings,
    inventory: [],
    allergenTerms: input.allergenTerms,
    dislikedIngredients: input.dislikedIngredients,
    dietaryRestrictions: input.dietaryRestrictions,
  };
  return (extra) =>
    generateHorizonDinners(input.weekStart, input.recipes, { ...base, ...extra }, input.metadata, range);
}

/**
 * A recipe offered for tonight on the direction screen. Only what the card
 * draws and what the recipe screen needs to open without a flash — it is not
 * a meal, and it is never written to the plan.
 */
export interface OnboardingIdea {
  id: string;
  title: string;
  translatedTitles?: { en?: string; fr?: string };
  image?: string;
  readyInMinutes: number;
  originalLanguage?: string;
}

export const TONIGHT_IDEAS = 3;
/**
 * "Pour ce soir" means a weeknight: a cook whose profile likes long recipes
 * still should not be offered a 70-minute dish on the tonight card. A recipe
 * with no known time is left out for the same reason.
 */
export const TONIGHT_MAX_MINUTES = 45;
const RELAXED_PASSES = 3;

/**
 * Three recipes the cook could make tonight, from the same profile as the week.
 *
 * #721: the second card on the direction screen was a drawing of a search bar,
 * so it read as a form to fill in and nobody chose it. It now shows real
 * answers. They come from the planner's own generator, so they honour the
 * same allergens and exclusions, and they leave out the week's dinners — the
 * two cards must not offer the same dish. Quickest first, and never over
 * TONIGHT_MAX_MINUTES: it is for tonight.
 * As with the week, the axes may be relaxed to find three; the allergens never.
 */
export function pickTonightIdeas(
  input: OnboardingWeekInput,
  weekRecipeIds: string[],
  count: number = TONIGHT_IDEAS,
): OnboardingIdea[] {
  const quick = input.recipes.filter((r) => r.readyInMinutes > 0 && r.readyInMinutes <= TONIGHT_MAX_MINUTES);
  if (quick.length === 0 || count <= 0) return [];
  const runPass = generatorPass({ ...input, recipes: quick });
  const lang = input.language;

  // A title the cook can read comes first — three English titles on a French
  // screen look like a broken app, not a suggestion. Then the pass that kept the
  // axes, then quickest, with an unknown time (0) last.
  type Candidate = { dinner: GeneratedDinner; relaxed: boolean };
  const readable = ({ dinner: d }: Candidate) =>
    !lang || d.recipe.originalLanguage === lang || !!d.recipe.translatedTitles?.[lang]?.trim();
  const minutes = ({ dinner: d }: Candidate) => d.recipe.readyInMinutes || Number.MAX_SAFE_INTEGER;
  const rank = (x: Candidate, y: Candidate) =>
    Number(readable(y)) - Number(readable(x))
    || Number(x.relaxed) - Number(y.relaxed)
    || minutes(x) - minutes(y);

  // The catalogue holds the same dish under more than one id, so dedupe on the
  // title too, or the card offers "Roasted Eggplant" twice.
  const titleKey = (d: GeneratedDinner) => decodeHtmlEntities(d.recipe.title).trim().toLowerCase();
  // The week's own dishes are out by title as well as by id.
  const weekIds = new Set(weekRecipeIds);
  const weekTitles = new Set(
    input.recipes.filter((r) => weekIds.has(r.id)).map((r) => decodeHtmlEntities(r.title).trim().toLowerCase()),
  );
  const pickFrom = (pool: Candidate[]): GeneratedDinner[] => {
    const ids = new Set<string>();
    const titles = new Set<string>(weekTitles);
    const out: GeneratedDinner[] = [];
    for (const c of [...pool].sort(rank)) {
      if (out.length >= count) break;
      if (ids.has(c.dinner.recipe.id) || titles.has(titleKey(c.dinner))) continue;
      ids.add(c.dinner.recipe.id);
      titles.add(titleKey(c.dinner));
      out.push(c.dinner);
    }
    return out;
  };

  const pool: Candidate[] = runPass({
    ...axesToDinnerConfig(input.axes, input.schedule),
    tasteAxes: input.axes,
    previouslyGeneratedRecipeIds: weekRecipeIds,
  }).map((dinner) => ({ dinner, relaxed: false }));
  // Relax the axes when they did not yield enough — or not enough the cook can
  // read. One pass is one dinner per day, so a library that is mostly in the
  // other language can need a few before three readable titles turn up.
  const short = () => {
    const picked = pickFrom(pool);
    return picked.length < count || picked.some((d) => !readable({ dinner: d, relaxed: false }));
  };
  for (let pass = 0; pass < RELAXED_PASSES && short(); pass++) {
    const more = runPass({
      mealSchedule: input.schedule,
      previouslyGeneratedRecipeIds: [...weekRecipeIds, ...pool.map((c) => c.dinner.recipe.id)],
    });
    if (more.length === 0) break;
    pool.push(...more.map((dinner) => ({ dinner, relaxed: true })));
  }

  // Shown quickest first: the choosing is done, and it is for tonight.
  const byTime = (x: GeneratedDinner, y: GeneratedDinner) =>
    minutes({ dinner: x, relaxed: false }) - minutes({ dinner: y, relaxed: false });
  return pickFrom(pool).sort(byTime).map(({ recipe }) => ({
    id: recipe.id,
    title: decodeHtmlEntities(recipe.title),
    ...(recipe.translatedTitles && { translatedTitles: recipe.translatedTitles }),
    ...(recipe.image && { image: recipe.image }),
    readyInMinutes: recipe.readyInMinutes || 0,
    ...(recipe.originalLanguage && { originalLanguage: recipe.originalLanguage }),
  }));
}

export interface OnboardingWeekResult {
  meals: PlannedMeal[];
  diagnostics: OnboardingDiagnostics;
  /** The direction screen's second card; empty when nothing fits the profile. */
  tonight: OnboardingIdea[];
}

/**
 * The same week for a different number of people. The week is built while the
 * profile beat plays, before the cook has confirmed the portions; the stepper
 * changes nothing about which dinners fit, only how many they serve.
 */
export function withServings(meals: PlannedMeal[], servings: number): PlannedMeal[] {
  return meals.map((m) => (m.servings === servings && m.recipe?.servings === servings
    ? m
    : { ...m, servings, ...(m.recipe && { recipe: { ...m.recipe, servings } }) }));
}

/** Translates one title; the app passes `recipeTranslationService.translateTitle`. */
export type TitleTranslator = (
  recipeId: string,
  title: string,
  originalLanguage: 'en' | 'fr',
  targetLanguage: 'en' | 'fr',
) => Promise<string>;

/** How many week dinners the direction screen's plan card shows. */
export const PLAN_PREVIEW_DINNERS = 3;

/**
 * Put the direction screen's titles in the cook's language before it shows.
 *
 * The catalogue carries a translated title on a minority of recipes, so the
 * plan card showed "Homemade fish fingers" and "Za’atar Roasted Tofu…" to a
 * French cook (#721), while the planner — which translates at render time —
 * would show the same dish in French a second later. This runs the planner's
 * own title path (device cache, then `recipeTranslations`, then the translate
 * endpoint, which stores what it makes) for the six titles the screen shows:
 * the first three dinners and tonight's ideas.
 *
 * It never holds the screen up past `deadlineMs`, and never fails: a title
 * that is not back in time, or whose translation failed, stays as it was.
 */
export async function localizeOnboardingTitles(
  meals: PlannedMeal[],
  tonight: OnboardingIdea[],
  language: 'en' | 'fr',
  translate: TitleTranslator,
  deadlineMs: number,
  /** Guesses a title's language when the catalogue recorded none ("unknown"). */
  detect: (title: string) => 'en' | 'fr' | 'unknown' = () => 'unknown',
): Promise<{ meals: PlannedMeal[]; tonight: OnboardingIdea[] }> {
  type Item = { id: string; title: string; originalLanguage?: string; translatedTitles?: { en?: string; fr?: string } };
  const sourceOf = (r: Item) =>
    (r.originalLanguage === 'en' || r.originalLanguage === 'fr' ? r.originalLanguage : detect(r.title));
  const needs = (r: Item) => {
    const from = sourceOf(r);
    return (from === 'en' || from === 'fr') && from !== language && !r.translatedTitles?.[language]?.trim();
  };

  const shown: Item[] = [
    ...meals.slice(0, PLAN_PREVIEW_DINNERS).flatMap((m) => (m.recipe
      ? [{ id: String(m.recipe.id), title: m.recipe.title, originalLanguage: m.recipe.originalLanguage, translatedTitles: m.recipe.translatedTitles }]
      : [])),
    ...tonight,
  ];
  const found = new Map<string, string>();
  const jobs = [...new Map(shown.filter(needs).map((r) => [r.id, r])).values()].map(async (r) => {
    try {
      const title = await translate(r.id, r.title, sourceOf(r) as 'en' | 'fr', language);
      if (title?.trim() && title !== r.title) found.set(r.id, title.trim());
    } catch {
      // The original title reads fine; the planner will try again.
    }
  });
  if (jobs.length === 0) return { meals, tonight };

  let timer: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([
    Promise.all(jobs),
    new Promise<void>((resolve) => { timer = setTimeout(resolve, deadlineMs); }),
  ]);
  if (timer) clearTimeout(timer);
  if (found.size === 0) return { meals, tonight };

  const withTitle = <T extends { translatedTitles?: { en?: string; fr?: string } }>(item: T, id: string): T =>
    found.has(id) ? { ...item, translatedTitles: { ...item.translatedTitles, [language]: found.get(id) } } : item;
  return {
    meals: meals.map((m) => (m.recipe && found.has(String(m.recipe.id)) ? { ...m, recipe: withTitle(m.recipe, String(m.recipe.id)) } : m)),
    tonight: tonight.map((i) => withTitle(i, i.id)),
  };
}

export interface PlannedMealOptions {
  servings: number;
  details?: Map<string, Recipe | null>;
  now?: Date;
}

export function toPlannedMeals(dinners: GeneratedDinner[], opts: PlannedMealOptions): PlannedMeal[] {
  const now = opts.now ?? new Date();
  return dinners.map((dinner, idx) => {
    const details = opts.details?.get(dinner.recipe.id) ?? null;
    return {
      id: `${dinner.slot.date}-dinner-${now.getTime()}-${idx}`,
      recipeId: dinner.recipe.id,
      recipe: {
        id: dinner.recipe.id,
        title: dinner.recipe.title,
        // The direction card and the planner resolve the title in the cook's language.
        ...(dinner.recipe.translatedTitles && { translatedTitles: dinner.recipe.translatedTitles }),
        // Required by translateMealPlanRecipes: meals without it are never translated.
        originalLanguage: dinner.recipe.originalLanguage,
        image: dinner.recipe.image || '',
        readyInMinutes: dinner.recipe.readyInMinutes,
        servings: opts.servings,
        healthScore: dinner.recipe.healthScore,
        dishTypes: dinner.recipe.dishTypes,
        cuisines: dinner.recipe.cuisines,
        diets: dinner.recipe.diets,
        extendedIngredients: details?.extendedIngredients?.length
          ? details.extendedIngredients
          : dinner.recipe.ingredients.map((name, i) => {
              const parsed = parseIngredientText(name);
              return {
                id: i,
                name: parsed.remainder || name,
                original: name,
                amount: parseAmount(name) ?? 0,
                unit: parsed.unitPart || '',
              };
            }),
        sourceUrl: dinner.recipe.sourceUrl,
        instructions: details?.instructions ?? [],
        ...(details?.instructionSections && { instructionSections: details.instructionSections }),
        summary: '',
      } as Recipe,
      dietaryRole: isFishRecipe(dinner.recipe) ? 'fish' : isVegetarianRecipe(dinner.recipe) ? 'vegetarian' : undefined,
      scheduledFor: dinner.slot.date,
      dayOfWeek: dinner.slot.dayOfWeek,
      mealType: 'dinner' as MealType,
      servings: opts.servings,
      createdAt: now.toISOString(),
      ingredientsReviewed: false,
      missingIngredients: [],
      matchMetadata: dinner.matchMetadata
        ? {
            inventoryMatchPercent: dinner.matchMetadata.inventoryMatchPercent,
            usesExpiringIngredients: dinner.matchMetadata.usesExpiringIngredients,
            isNewRecipe: dinner.matchMetadata.isNewRecipe,
            constraintLabel: dinner.matchMetadata.constraintLabel,
          }
        : undefined,
    };
  });
}

/**
 * Picks the dinners, fetches their details so instructions are on the row,
 * and returns meal-plan rows. Never throws on a missing detail: the row keeps
 * the index ingredients and an empty instruction list, as the planner does.
 */
export async function buildOnboardingWeek(input: OnboardingWeekInput): Promise<OnboardingWeekResult> {
  const { dinners, diagnostics } = planOnboardingDinners(input);
  const details = new Map<string, Recipe | null>();
  await Promise.allSettled(
    dinners.map(async (d) => {
      try {
        details.set(d.recipe.id, await plannerRecipeDetails(d.recipe.id));
      } catch {
        details.set(d.recipe.id, null);
      }
    }),
  );
  const tonight = pickTonightIdeas(input, dinners.map((d) => d.recipe.id));
  return { meals: toPlannedMeals(dinners, { servings: input.servings, details }), diagnostics, tonight };
}
